import React, { useEffect, useMemo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import { useDispatch, useSelector } from 'react-redux';
import Email from '../../../../../assets/icons/flows/email.svg';
import Edit from '../../../../../assets/icons/flows/edit.svg';
import Alert from '../../../../../assets/icons/flows/alert.svg';
import {
  clearActiveSettings,
  deleteSettings,
  selectConfigsList,
  selectErrors,
  selectFlowStatus,
  selectSenderEmail,
  setActiveSettings,
  setSenderEmailSetUp,
  setSendGridApiKey,
} from '../../../../../store/reducers/flows';
import Delete from '../../../../../assets/icons/flows/delete.svg';
import { handle, handleInput } from '../../../../../utils/flows';
import Tooltip from '../../../../../components/ui/Tooltip';
import Divider from '../../../../../assets/icons/divider.svg';
import styles from './EmailNode.module.scss';
import { userApi } from '../../../../../api/user';
import { showErrorMessage } from '../../../../../components/base/Notifications';
import Spinner from '../../../../../components/base/Spinner';

const EmailNode = ({ ...props }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState();
  const [completed, setCompleted] = useState(false);
  const [onNodeHover, setOnNodeHover] = useState(false);

  const configList = useSelector(selectConfigsList);
  const errors = useSelector(selectErrors);
  const status = useSelector(selectFlowStatus);
  const isSenderEmailSetUp = useSelector(selectSenderEmail);

  const [teamKeys, result] = userApi.useGetTeamKeysMutation();

  const filteredErrors = () =>
    errors.length && errors.filter((elem) => elem.node_id === props.id);

  const handleEdit = useMemo(() => {
    if (result.isLoading || result.isFetching) {
      return (
        <div className="d-flex align-item-center">
          <Spinner />
        </div>
      );
    }
    return (
      <div
        className="cursor-pointer"
        role="presentation"
        onClick={(e) => {
          e.preventDefault();
          teamKeys();
        }}
      >
        <Edit />
      </div>
    );
  }, [result?.isFetching, result?.isLoading, teamKeys]);

  useEffect(() => {
    if (configList.length) {
      const index = configList
        .map((object) => object.node_id)
        .indexOf(props.id);
      if (index >= 0) {
        setData(configList[index]);
      }
    }
  }, [configList, props.id]);

  useEffect(() => {
    if (data && data?.template && data.name && isSenderEmailSetUp) {
      setCompleted(true);
    } else {
      setCompleted(false);
    }
  }, [data, isSenderEmailSetUp]);

  useEffect(() => {
    if (result.isSuccess && result.data) {
      if (result.data.send_grid_api_key) {
        dispatch(setActiveSettings({ type: 'emailNode', node_id: props.id }));
      }
      if (!result.data.send_grid_api_key) {
        dispatch(setSendGridApiKey(result.data.send_grid_api_key));
      }
      dispatch(setSenderEmailSetUp(result.data.sender_email));
      result.reset();
    }
    if (result.isError) {
      showErrorMessage(result?.error?.data?.detail || 'Something went wrong');
      result.reset();
    }
  }, [dispatch, props?.id, result]);

  return (
    <>
      <div
        className={`
        ${styles.wrapper}
        ${data && data?.template ? styles.size120 : ''}
        ${status === 'running' || status === 'scheduled' || status === 'stopped' ? 'cursor-pointer' : ''}
        d-flex
        flex-column
        justify-content-center
        `}
        role="presentation"
        onMouseEnter={() => setOnNodeHover(true)}
        onMouseLeave={() => setOnNodeHover(false)}
        onClick={() => {
          if (
            status === 'running' ||
            status === 'scheduled' ||
            status === 'stopped'
          ) {
            dispatch(
              setActiveSettings({ type: 'emailNode', node_id: props.id })
            );
          }
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-2 w-100">
          <div
            className={`${styles.title_size} d-flex align-items-center gap-1`}
          >
            {status !== 'running' &&
            status !== 'stopped' &&
            status !== 'scheduled' ? (
              <>
                {!completed || filteredErrors().length ? (
                  <>
                    <div
                      className={styles.alert}
                      data-for={`${props.id}_error`}
                      data-tip
                    >
                      <Alert />
                    </div>
                    {filteredErrors().length ? (
                      <Tooltip
                        id={`${props.id}_error`}
                        info={`${filteredErrors()[0].detail}`}
                      />
                    ) : null}
                  </>
                ) : null}
                <div
                  className={onNodeHover ? styles.delete : styles.delete_hide}
                >
                  <Delete
                    onClick={() => {
                      props.data.onRemove(props.id);
                      dispatch(deleteSettings(props.id));
                      dispatch(clearActiveSettings());
                    }}
                  />
                </div>
              </>
            ) : null}
            <div width="32px" height="32px">
              <Email />
            </div>
            <div className={`${styles.description} text-truncate`}>
              {(data && data?.name) || 'Email'}
            </div>
          </div>
          {handleEdit}
        </div>
        {data && data?.template ? (
          <div>
            <div className={`${styles.divider} d-flex`}>
              <Divider />
            </div>
            <div className={`${styles.info} d-flex`}>
              <div
                className={`${styles.info_col} d-flex flex-column gap-1 w-100`}
              >
                <div>Template:</div>
                <div className={`${styles.template} text-truncate`}>
                  {data.template}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <Handle
        type="target"
        position={Position.Left}
        style={{ ...handleInput, left: '-10px' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ ...handle, right: '-16px' }}
        id="emailNode_output"
      />
    </>
  );
};

export default EmailNode;
